import './Toast.scss';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const ToastContext = createContext({ show: () => {} });

/**
 * Transient toast notification provider. Hoisted inside GlobalStateProvider
 * in App.jsx so any component can call useToast() regardless of the app's
 * auth state. Toasts auto-dismiss after `duration` ms (default 1500).
 */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);

    const show = useCallback((message, opts = {}) => {
        if (!message) return;
        const id = ++nextId.current;
        const tone = opts.tone || 'info';
        const duration = opts.duration ?? 1500;
        setToasts((prev) => [...prev, { id, message, tone }]);
        if (duration > 0) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        }
    }, []);

    return (
        <ToastContext.Provider value={{ show }}>
            {children}
            {typeof document !== 'undefined'
                ? ReactDOM.createPortal(
                    <div className="cw-Toast__stack" role="status" aria-live="polite">
                        {toasts.map((t) => (
                            <div key={t.id} className={`cw-Toast cw-Toast--${t.tone}`}>
                                {t.message}
                            </div>
                        ))}
                    </div>,
                    document.body
                )
                : null}
        </ToastContext.Provider>
    );
}

/**
 * @returns {{ show: (message: string, opts?: { tone?: 'info'|'ok'|'warn'|'err', duration?: number }) => void }}
 */
export function useToast() {
    return useContext(ToastContext);
}
